import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CForm,
  CFormInput,
  CButton,
  CAlert,
  CSpinner,
  CInputGroup
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilUser, cilLockLocked, cilUserPlus, cilCheckCircle, cilShieldAlt } from '@coreui/icons';
import { authAPI } from '../../services/api';
import SecureCaptcha from '../../components/SecureCaptcha';
import ChangePasswordModal from './ChangePasswordModal';

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const location = useLocation();

  // Check if session expired from location state
  useEffect(() => {
    if (location.state?.sessionExpired) {
      setSessionExpired(true);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError('');
    if (sessionExpired) setSessionExpired(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSessionExpired(false);

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setShowCaptcha(true);
  };

  const handleCaptchaSuccess = async () => {
    setShowCaptcha(false);
    setLoading(true);

    try {
      // Try super admin login first (for super admin credentials)
      // Then try regular login if super admin fails
      let response;
      
      // First try super admin login
      try {
        response = await authAPI.superAdminLogin(formData.email, formData.password);
       
        if (response.data.success) {
          localStorage.setItem('token', response.data.access_token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          
          // For super admin, set is_super_admin to true
          localStorage.setItem('userInfo', JSON.stringify({
            ...response.data.user,
            is_super_admin: true,
            user_type: 'super_admin'
          }));
          
          onLogin(response.data.user);
          return;
        }
      } catch (superAdminError) {
        // If super admin login fails, try regular login
      }
      
      // Try regular login
      response = await authAPI.login(formData.email, formData.password);
      
      if (response.data.success) {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.reseller));
        
        // Store user info for profile page
        if (response.data.user) {
          localStorage.setItem('userInfo', JSON.stringify(response.data.user));
        } else {
          // Fallback for backward compatibility
          localStorage.setItem('userInfo', JSON.stringify({
            id: response.data.reseller.id,
            name: response.data.reseller.name,
            email: response.data.reseller.email,
            role: 'admin',
            user_type: 'admin',
            is_admin: true,
            is_super_admin: false // Regular users are not super admin
          }));
        }

        onLogin(response.data.reseller);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCaptchaClose = () => {
    setShowCaptcha(false);
  };

  const handleBecomeReseller = () => {
    window.open('https://www.lizardtech.com/reseller-request', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="login-container d-flex align-items-center">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md={10} lg={8}>
            <CRow className="g-4">
              {/* Login Form Column */}
              <CCol md={6}>
                <CCard className="h-100">
                  <CCardHeader className="text-center bg-transparent border-0 pt-4">
                    <CIcon icon={cilUser} size="3xl" className="text-primary mb-3" />
                    <h4>Welcome Back</h4>
                    <p className="text-medium-emphasis">Sign in to your account</p>
                  </CCardHeader>
                  <CCardBody className="p-4">
                    
                    {/* Error Alert */}
                    {error && (
                      <CAlert color="danger" className="mb-3">
                        {error}
                      </CAlert>
                    )}
                    
                    <CForm onSubmit={handleSubmit}>
                      <div className="mb-3">
                        <CFormInput
                          type="email"
                          name="email"
                          placeholder="Email Address"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      
                      <div className="mb-4">
                        <CInputGroup>
                          <CFormInput
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="Password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                          />
                          <CButton
                            type="button"
                            variant="outline"
                            onClick={togglePasswordVisibility}
                            style={{ minWidth: '80px' }}
                          >
                            {showPassword ? 'Hide' : 'Show'}
                          </CButton>
                        </CInputGroup>
                      </div>
                      
                      <CButton 
                        type="submit" 
                        color="primary" 
                        className="w-100 py-2 mb-3"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <CSpinner size="sm" className="me-2" />
                            Signing In...
                          </>
                        ) : (
                          'Sign In'
                        )}
                      </CButton>

                      {/* Change Password Button */}
                      <CButton 
                        color="link" 
                        className="w-100 text-decoration-none mb-3"
                        onClick={() => setShowChangePassword(true)}
                      >
                        <CIcon icon={cilLockLocked} className="me-1" size="sm" />
                        Change Password
                      </CButton>
                    </CForm>

                    {/* Terms and Privacy Links */}
                    <div className="text-center mt-4 pt-3 border-top">
                      <small className="text-medium-emphasis">
                        By signing in, you agree to our{' '}
                        <a 
                          href="https://www.geowgs84.com/terms-of-use" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          Terms of Use
                        </a>{' '}
                        and{' '}
                        <a 
                          href="https://www.geowgs84.com/privacy-policy" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          Privacy Policy
                        </a>
                      </small>
                    </div>
                  </CCardBody>
                </CCard>
              </CCol>

              {/* Register Promotion Column */}
              <CCol md={6}>
                <CCard className="h-100 bg-primary text-white">
                  <CCardBody className="d-flex flex-column justify-content-center p-5 text-center">
                    <CIcon icon={cilUserPlus} size="4xl" className="mb-4" />
                    <h3>New to Partner Portal?</h3>
                    <p className="mb-4 opacity-75">
                      Join our growing network of partners and unlock exclusive benefits for your business.
                    </p>
                    
                    <div className="benefits-list text-start mb-4">
                      <div className="benefit-item d-flex align-items-center mb-2">
                        <CIcon icon={cilCheckCircle} className="text-success me-2" />
                        <span>Access to exclusive deals</span>
                      </div>
                      <div className="benefit-item d-flex align-items-center mb-2">
                        <CIcon icon={cilCheckCircle} className="text-success me-2" />
                        <span>Manage your clients efficiently</span>
                      </div>
                      <div className="benefit-item d-flex align-items-center mb-2">
                        <CIcon icon={cilCheckCircle} className="text-success me-2" />
                        <span>Real-time analytics & reports</span>
                      </div>
                      <div className="benefit-item d-flex align-items-center">
                        <CIcon icon={cilCheckCircle} className="text-success me-2" />
                        <span>Priority support</span>
                      </div>
                    </div>

                    <CButton 
                      color="light" 
                      className="w-100 py-2 fw-semibold"
                      onClick={handleBecomeReseller}
                    >
                      <CIcon icon={cilUserPlus} className="me-2" />
                      Become GeoExpress Reseller
                    </CButton>

                    {/* Terms and Privacy Links for Register Section */}
                    <div className="text-center mt-4 pt-3 border-top border-light">
                      <small className="opacity-75">
                        By registering, you agree to our{' '}
                        <a 
                          href="https://www.geowgs84.com/terms-of-use" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-light text-decoration-none"
                        >
                          Terms of Use
                        </a>{' '}
                        and{' '}
                        <a 
                          href="https://www.geowgs84.com/privacy-policy" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-light text-decoration-none"
                        >
                          Privacy Policy
                        </a>
                      </small>
                    </div>
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>
          </CCol>
        </CRow>
      </CContainer>

      {/* Secure Captcha Modal for Login */}
      <SecureCaptcha
        show={showCaptcha}
        onSuccess={handleCaptchaSuccess}
        onClose={handleCaptchaClose}
        title="Security Verification"
        description="Please complete the security check to sign in to your account."
      />

      {/* Change Password Modal */}
      <ChangePasswordModal 
        show={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
    </div>
  );
};

export default Login;